import { CheckCircle2, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { useSlackSync } from "@/features/people/hooks/use-imports";
import {
  useDisconnectSlack,
  useSlackStatus,
  useStartSlackOauth,
} from "@/features/people/hooks/use-slack-connection";
import { Button } from "@/shared/components/ui/button";

interface Props {
  workspaceId: string;
  /** Notified with the import summary on a successful sync so the dialog can
   *  flip to its result step (matches CSV/Sheet flows). */
  onSynced: (result: { created: number; updated: number; errors: number }) => void;
}

/**
 * Slack source for the Add-users dialog. Drives the full OAuth + import flow:
 * 1. If not connected → "Connect Slack" opens the Slack OAuth in a new tab.
 *    Slack redirects to /auth/slack/callback on the shore which redirects
 *    back to the people page with `?slack=connected`, the user closes that
 *    tab, the status query refetches, and step 2 unlocks.
 * 2. If connected → "Sync members" pulls users.list through the existing
 *    CSV-import pipeline server-side and reports created/updated counts.
 */
export function SlackImportForm({ workspaceId, onSynced }: Props) {
  const status = useSlackStatus(workspaceId);
  const startOauth = useStartSlackOauth(workspaceId);
  const disconnect = useDisconnectSlack(workspaceId);
  const sync = useSlackSync(workspaceId);

  // Refetch status when the window regains focus, covers the OAuth round-trip
  // happening in a separate tab. (Connect Slack → click → tab opens →
  // returning to this tab refetches → "Connected" shows.)
  useEffect(() => {
    function onFocus() {
      void status.refetch();
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [status]);

  function handleConnect() {
    startOauth.mutate(undefined, {
      onSuccess: ({ authUrl }) => {
        window.open(authUrl, "_blank", "noopener,noreferrer");
      },
    });
  }

  function handleSync() {
    sync.mutate(undefined, {
      onSuccess: (r) =>
        onSynced({
          created: r.created,
          updated: r.updated,
          errors: r.errors,
        }),
    });
  }

  if (status.isLoading) {
    return <p className="text-sm text-text-tertiary">Checking Slack connection…</p>;
  }

  const connected = status.data?.connected === true;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-text-secondary">
        Pull your Slack workspace's members directly. Pulsewise reads the active humans on your
        Slack team (excluding bots and deactivated users) and adds them to People.
      </p>

      {connected ? (
        <ConnectedState
          slackTeamId={status.data?.slackTeamId}
          syncing={sync.isPending}
          onSync={handleSync}
          onDisconnect={() => disconnect.mutate()}
          disconnecting={disconnect.isPending}
        />
      ) : (
        <DisconnectedState
          connecting={startOauth.isPending}
          onConnect={handleConnect}
          error={startOauth.error}
        />
      )}

      {sync.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {sync.error instanceof Error ? sync.error.message : "Slack sync failed"}
        </p>
      ) : null}
    </div>
  );
}

function DisconnectedState({
  connecting,
  onConnect,
  error,
}: {
  connecting: boolean;
  onConnect: () => void;
  error: unknown;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Button type="button" disabled={connecting} onClick={onConnect}>
        <ExternalLink className="size-3.5" aria-hidden="true" />
        {connecting ? "Opening Slack…" : "Connect Slack"}
      </Button>
      <p className="text-xs text-text-tertiary">
        Opens slack.com in a new tab. Approve the install, Pulsewise needs <code>users:read</code>{" "}
        and <code>users:read.email</code> to fetch your team. Return here when you're done.
      </p>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Couldn't start Slack OAuth"}
        </p>
      ) : null}
    </div>
  );
}

function ConnectedState({
  slackTeamId,
  syncing,
  onSync,
  onDisconnect,
  disconnecting,
}: {
  slackTeamId: string | null | undefined;
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded-md border border-success/40 bg-success-bg px-3 py-2 text-sm text-success-fg">
        <CheckCircle2 className="size-4" aria-hidden="true" />
        <span>Connected{slackTeamId ? ` (team ${slackTeamId})` : ""}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" disabled={syncing} onClick={onSync}>
          {syncing ? "Syncing…" : "Sync members from Slack"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={disconnecting || syncing}
          onClick={onDisconnect}
        >
          {disconnecting ? "Disconnecting…" : "Disconnect"}
        </Button>
      </div>
    </div>
  );
}
